import { useState, useMemo } from "react";
import {
  List,
  TableColumnType,
  Button,
  Table,
  SaveButton,
  Input,
  FilterDropdown,
  FilterDropdownProps,
  TextField,
  EditButton,
  useTable,
} from "@pankod/refine-antd";
import { useCustomMutation, useExport, useNotification } from "@pankod/refine-core";
import { renderNumber, renderString } from "utils/render";
import { REST_PUBLIC_URI } from "environment";

export const UserStatList = () => {
  const { TextArea } = Input;
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const [remark, setRemark] = useState<string>("");
  const { open } = useNotification();

  const { tableProps, filters, tableQueryResult: { refetch }  } = useTable({
    queryOptions: { retry: false },
    initialFilter: [
      {
        field: "email",
        operator: "eq",
        value: "",
      },
    ],
  });
  const { triggerExport } = useExport({
    filters: filters,
    mapData: (item) => {
      return {
        ...item,
        created_at: new Date(item.created_at).toISOString().split("T")[0],
      };
    },
  });

  const { mutate } = useCustomMutation();

  const handleSaveRemark = (userId: number, remark: string) => {
    mutate(
      {
        url: `${REST_PUBLIC_URI}/api/admin/v1/user-stats/remark`,
        method: "put",
        values: {
          'user_id': userId,
          'remark': remark,
        },
      },
      {
        onSuccess: () => {
          refetch();
          // open notification
          open?.({
            type: "success",
            message: "Success",
            description: "Remark saved successfully",
          });

        },
      },
    );
  }

  const columns = useMemo<TableColumnType<any>[]>(
    () => [
      {
        dataIndex: "user_id",
        title: "User Id",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        ellipsis: {
          showTitle: false,
        },
        fixed: "left",
      },
      {
        dataIndex: "email",
        title: "Email",
        width: 200,
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <FilterDropdown {...props}>
              <Input
                placeholder="Search email"
                onPressEnter={(e) => {
                  props.confirm();
                }}
              />
            </FilterDropdown>
          );
        },
      },
      {
        dataIndex: "name",
        title: "Name",
        width: 200,
      },
      {
        dataIndex: "created_at",
        title: "Date Created",
        width: 150,
        render: (value: string) => {
          return new Date(value).toISOString().split("T")[0];
        },
      },
      {
        dataIndex: "tier",
        title: "Tier",
        width: 200,
      },
      {
        dataIndex: "tier_time_left",
        title: "Tier Time Left",
        width: 200,
        render: (_, record) => {
          let days = record.tier_time_left.split(" ")[0];
          const style = {
            color: days < 30 ? "red" : "black",
          };
          return renderString(record.tier_time_left, undefined, { style });
        },
      },
      {
        dataIndex: "credit",
        title: "Credit",
        width: 200,
        render: (value: string) => {
          const style = {
            textAlign: "right",
          };
          return renderNumber(value, 2, undefined, { style });
        },
      },
      {
        dataIndex: "initial_fund",
        title: "Initial Fund",
        width: 200,
        render: (value: string) => {
          const style = {
            textAlign: "right",
          };
          return renderNumber(value, 2, undefined, { style });
        },
      },
      {
        dataIndex: "current_fund",
        title: "Current Fund",
        width: 200,
        render: (value: string) => {
          const style = {
            textAlign: "right",
          };
          return renderNumber(value, 2, undefined, { style });
        },
      },
      {
        dataIndex: "bot_running",
        title: "Bot Running",
        width: 100,
        render: (value: string) => {
          const style = {
            textAlign: "right",
          };
          return renderNumber(value, 0, undefined, { style });
        },
      },
      {
        dataIndex: "remark",
        title: "Remark",
        width: 250,
        fixed: "right",
        align: "center",
        render: (value, record) => {
          if (record.user_id === editId) {
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextArea
                  placeholder="Remark for user"
                  style={{ minWidth: 100, marginRight: "10px" }}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  defaultValue={value}
                  onChange={(e) => {
                    setRemark(e.target.value);
                  }}

                />
                <div style={{ display: "flex", justifyContent: "end"}}>
                  <SaveButton
                    hideText
                    size="small"
                    style={{ marginRight: "3px", padding: "0px 4px 0px 4px" }}
                    onClick={() => {
                      handleSaveRemark(record.user_id, remark);
                      setEditId(undefined);
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setEditId(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            );
          }
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                alignContent: "center",
                justifyContent: "space-between"
              }}
            >
              <TextField
                value={value ? value : "-"}
                style={{ minWidth: 100, textAlign: "left" , marginLeft: "10px"}}
              />
              <EditButton
                hideText
                size="small"
                onClick={() => {
                  setEditId(record.user_id);
                }}
              />
            </div>
          );
        },
      },
    ],
    [TextArea, editId, handleSaveRemark, remark]
  );
  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button type="primary" onClick={triggerExport}>
            EXPORT
          </Button>
        </>
      )}
    >
      <Table
        {...tableProps}
        bordered={true}
        rowKey="user_id"
        columns={columns}
        scroll={{ x: 1500 }}
        size="middle"
        pagination={{
          ...tableProps.pagination,
          position: ["bottomRight"],
          size: "default",
        }}
      ></Table>
    </List>
  );
};
